// Helm.ai brand theme for Material UI
// Based on colors extracted from thehelm.ai website

import { createTheme } from '@mui/material/styles';

// Helm brand colors
const helmColors = {
  purple: '#845BA3',
  turquoise: '#47BDC6', 
  yellow: '#EBDD71',
  cararra: '#F8F8F8', // Light background
  borderColor: '#CFD2AD',
  blue: '#2196F3', // Fallback blue
  black: '#000000',
  white: '#ffffff',
  gray: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121'
  }
};

const helmTheme = createTheme({
  palette: {
    primary: {
      main: helmColors.turquoise,
      light: '#6BCDD4',
      dark: '#2A8A91',
      contrastText: helmColors.white
    },
    secondary: {
      main: helmColors.purple,
      light: '#A67BB5',
      dark: '#5D3F72',
      contrastText: helmColors.white
    },
    tertiary: {
      main: helmColors.yellow,
      light: '#F0E68C',
      dark: '#DAC84F',
      contrastText: helmColors.black
    },
    background: {
      default: helmColors.white,
      paper: helmColors.cararra,
      secondary: helmColors.gray[50]
    },
    text: {
      primary: helmColors.black,
      secondary: helmColors.gray[700],
      disabled: helmColors.gray[500]
    },
    divider: helmColors.borderColor,
    success: {
      main: '#4CAF50',
      light: '#81C784',
      dark: '#388E3C'
    },
    warning: {
      main: helmColors.yellow,
      light: '#F0E68C', 
      dark: '#DAC84F'
    },
    error: {
      main: '#F44336',
      light: '#E57373',
      dark: '#D32F2F'
    },
    info: {
      main: helmColors.turquoise,
      light: '#6BCDD4',
      dark: '#2A8A91'
    }
  },
  typography: {
    fontFamily: '"Satoshi", "Satoshi Placeholder", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: helmColors.black
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: helmColors.black
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: helmColors.black
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: helmColors.black
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: helmColors.black
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: helmColors.black
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: helmColors.black
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: helmColors.gray[700]
    },
    button: {
      textTransform: 'none',
      fontWeight: 600
    }
  },
  shape: {
    borderRadius: 10 // Matches Helm's 10px border radius
  },
  shadows: [
    'none',
    '0px 4px 4px 0px rgba(0, 0, 0, 0.05)', // Matches Helm's box shadow
    '0px 4px 8px 0px rgba(0, 0, 0, 0.08)',
    '0px 6px 12px 0px rgba(0, 0, 0, 0.1)',
    '0px 8px 16px 0px rgba(0, 0, 0, 0.12)',
    '0px 10px 20px 0px rgba(0, 0, 0, 0.14)',
    '0px 12px 24px 0px rgba(0, 0, 0, 0.16)',
    '0px 14px 28px 0px rgba(0, 0, 0, 0.18)',
    '0px 16px 32px 0px rgba(0, 0, 0, 0.2)',
    '0px 18px 36px 0px rgba(0, 0, 0, 0.22)',
    '0px 20px 40px 0px rgba(0, 0, 0, 0.24)',
    '0px 22px 44px 0px rgba(0, 0, 0, 0.26)',
    '0px 24px 48px 0px rgba(0, 0, 0, 0.28)',
    '0px 26px 52px 0px rgba(0, 0, 0, 0.3)',
    '0px 28px 56px 0px rgba(0, 0, 0, 0.32)',
    '0px 30px 60px 0px rgba(0, 0, 0, 0.34)',
    '0px 32px 64px 0px rgba(0, 0, 0, 0.36)',
    '0px 34px 68px 0px rgba(0, 0, 0, 0.38)',
    '0px 36px 72px 0px rgba(0, 0, 0, 0.4)',
    '0px 38px 76px 0px rgba(0, 0, 0, 0.42)',
    '0px 40px 80px 0px rgba(0, 0, 0, 0.44)',
    '0px 42px 84px 0px rgba(0, 0, 0, 0.46)',
    '0px 44px 88px 0px rgba(0, 0, 0, 0.48)',
    '0px 46px 92px 0px rgba(0, 0, 0, 0.5)',
    '0px 48px 96px 0px rgba(0, 0, 0, 0.52)'
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.1)'
          }
        },
        contained: {
          background: `linear-gradient(90deg, ${helmColors.purple} 0%, ${helmColors.turquoise} 56%, ${helmColors.yellow} 100%)`,
          color: helmColors.white,
          '&:hover': {
            background: `linear-gradient(90deg, ${helmColors.purple} 0%, ${helmColors.turquoise} 40%, ${helmColors.yellow} 100%)`,
            transform: 'scale(1.02)'
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
          border: `1px solid ${helmColors.borderColor}`,
          '&:hover': {
            boxShadow: '0px 6px 12px 0px rgba(0, 0, 0, 0.1)'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500
        },
        colorPrimary: {
          backgroundColor: helmColors.turquoise,
          color: helmColors.white
        },
        colorSecondary: {
          backgroundColor: helmColors.purple,
          color: helmColors.white
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '& fieldset': {
              borderColor: helmColors.borderColor
            },
            '&:hover fieldset': {
              borderColor: helmColors.turquoise
            },
            '&.Mui-focused fieldset': {
              borderColor: helmColors.turquoise
            }
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 10
        }
      }
    }
  }
});

export default helmTheme;

// Additional color utilities
export const getChannelColor = (channel) => {
  const channelColors = {
    'DTC Web': helmColors.turquoise,
    'Wholesale': helmColors.purple,
    'Retail Media': helmColors.yellow,
    'Social Ads': '#FF6B6B',
    'Influencer': '#4ECDC4',
    'Email': '#45B7D1'
  };
  return channelColors[channel] || helmColors.gray[500];
};

export const getKPIColor = (value, type) => {
  switch (type) {
    case 'roi':
      return value > 2 ? helmColors.turquoise : value > 1 ? helmColors.yellow : '#FF6B6B';
    case 'margin':
      return value > 50 ? helmColors.turquoise : value > 30 ? helmColors.yellow : '#FF6B6B';
    case 'sellthrough':
      return value > 70 ? helmColors.turquoise : value > 50 ? helmColors.yellow : '#FF6B6B';
    default:
      return helmColors.turquoise;
  }
};


